import { FC, useEffect, useState } from 'react';
import {
  FiltersBox,
  ModalContent,
  ModalControlBox,
  ModalHead,
  ModalWrapper,
  StyledButton,
  StyledControlButton,
} from './styled';

import { Badge, Box, Drawer, IconButton, Modal, Typography } from '@mui/material';

import { CATEGORY_ORDER, MAX_FILTERS_RANGE_VALUES, COLORS } from '@/constants';
import {
  AltbauFilterIcon,
  BalkonFilterIcon,
  BarrierefreiFilterIcon,
  CheckboxGroupMenu,
  ClearFilterIcon,
  CrossIcon,
  FilterResetIcon,
  FiltersIcon,
  GarageFilterIcon,
  GartenFilterIcon,
  HouseKeyIcon,
  IconToggleGroup,
  KellerFilterIcon,
  KucheFilterIcon,
  LandMultilevelCheckboxList,
  LiftFilterIcon,
  LocationAutocomplete,
  NeubauFilterIcon,
  Range,
  TabMapIcon,
  TerrasseFilterIcon,
} from '@/components';
import { ArrowDown } from '@/components/icons/ArrowDown';
import { getClampToMax } from '@/common';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import {
  Category,
  Equipment,
  OfferType,
  OfferTypeObject,
  RangesDimensions,
  ReferenceLocation,
  UnitsMultilevelList,
} from '@/types';
import { MapArea } from '@propileya/geo-widget';

const FilterMapPreview = dynamic(
  () =>
    import('@/components/shared/FilterMapPreview').then((m) => ({ default: m.FilterMapPreview })),
  { ssr: false },
);

export interface FiltersProps {
  useMobileDrawer?: boolean;
  activeFiltersCount?: number;
  numFound?: number;
  categories: Array<Category>;
  categoriesValue: Array<Category>;
  setCategoriesValue: (categories: Array<Category>) => void;
  unitListValue: Array<string>;
  setUnitListValue: (value: string[]) => void;
  unitList?: UnitsMultilevelList;
  areaValue: number[];
  setAreaValue: (value: number[]) => void;
  rangesDimensions: RangesDimensions;
  equipment: Array<Equipment>;
  equipmentValue: Array<Equipment>;
  setEquipmentValue: (categories: Array<Equipment>) => void;
  roomNumberValue: number[];
  setRoomNumberValue: (value: number[]) => void;
  priceValue: number[];
  setPriceValue: (value: number[]) => void;
  rentPriceValue: number[];
  setRentPriceValue: (value: number[]) => void;
  powerConsumptionClassesList: Category[];
  powerConsumptionClasses: string[];
  setPowerConsumptionClasses: (value: string[]) => void;
  energyClassFactorsList: Category[];
  energyClassFactors: string[];
  setEnergyClassFactors: (value: string[]) => void;
  offerTypeList: OfferTypeObject[];
  offerTypeValue: OfferType;
  setOfferTypeValue: (value: OfferTypeObject[]) => void;
  onFullResetFilters: () => void;
  locations: ReferenceLocation[];
  onLocationsChange: (locations: ReferenceLocation[]) => void;
  onGoToMap?: () => void;
  onClearMapFilters?: () => void;
  isLand: boolean;
  isMap?: boolean;
  hasMapSelection?: boolean;
  mapArea?: MapArea | null;
  admunitIds?: string[];
  disabled?: boolean;
  canReset?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const Filters: FC<FiltersProps> = ({
  useMobileDrawer = false,
  activeFiltersCount = 0,
  numFound,
  onFullResetFilters,
  categories,
  categoriesValue,
  setCategoriesValue,
  equipment,
  equipmentValue,
  setEquipmentValue,
  setUnitListValue,
  unitListValue,
  rangesDimensions,
  areaValue,
  setAreaValue,
  roomNumberValue,
  setRoomNumberValue,
  priceValue,
  setPriceValue,
  rentPriceValue,
  setRentPriceValue,
  powerConsumptionClassesList,
  powerConsumptionClasses,
  setPowerConsumptionClasses,
  energyClassFactorsList,
  energyClassFactors,
  setEnergyClassFactors,
  unitList,
  offerTypeList,
  offerTypeValue,
  setOfferTypeValue,
  locations,
  onLocationsChange,
  onClearMapFilters,
  isLand,
  isMap = false,
  hasMapSelection = false,
  mapArea,
  admunitIds,
  disabled = false,
  canReset = false,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const t = useTranslations();

  useEffect(() => {
    if (!useMobileDrawer) return;
    if (isOpen) {
      document.documentElement.style.touchAction = 'none';
      document.documentElement.style.overscrollBehavior = 'none';
    } else {
      document.documentElement.style.touchAction = '';
      document.documentElement.style.overscrollBehavior = '';
    }
    return () => {
      document.documentElement.style.touchAction = '';
      document.documentElement.style.overscrollBehavior = '';
    };
  }, [isOpen, useMobileDrawer]);

  const handleClose = () => {
    setIsOpen(false);
    onOpenChange?.(false);
  };
  const handleOpen = () => {
    setIsOpen(true);
    onOpenChange?.(true);
  };

  const chosenOfferType = offerTypeList.filter((e) => e.code === offerTypeValue);

  const catVal = categoriesValue;
  const setCatVal = setCategoriesValue;
  const equipVal = equipmentValue;
  const setEquipVal = setEquipmentValue;
  const unitVal = unitListValue;
  const setUnitVal = setUnitListValue;
  const areaVal = areaValue;
  const setAreaVal = setAreaValue;
  const roomVal = roomNumberValue;
  const setRoomVal = setRoomNumberValue;
  const priceVal = priceValue;
  const setPriceVal = setPriceValue;
  const rentVal = rentPriceValue;
  const setRentVal = setRentPriceValue;
  const hwbVal = powerConsumptionClasses;
  const setHwbVal = setPowerConsumptionClasses;
  const fgeeVal = energyClassFactors;
  const setFgeeVal = setEnergyClassFactors;
  const offerVal = offerTypeValue;
  const setOfferVal = setOfferTypeValue;

  const effectiveIsLand = isLand;

  const filterContent = (
    <>
      <ModalHead>
        <Typography
          fontSize={24}
          fontWeight={600}
          lineHeight="34px"
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {t('searchResult.filter')}
        </Typography>
        <IconButton
          onClick={handleClose}
          aria-label={t('searchResult.filter')}
          sx={{
            flexShrink: 0,
            minWidth: 40,
            minHeight: 40,
            padding: 1,
            color: COLORS['secondary/500'],
            backgroundColor: 'transparent',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', color: COLORS['secondary/700'] },
          }}
        >
          <CrossIcon fill={COLORS['secondary/500']} />
        </IconButton>
      </ModalHead>

      <ModalContent>
        <IconToggleGroup
          options={[
            {
              value: offerTypeList[0],
              title: offerTypeList[0]?.title,
            },
            {
              value: offerTypeList[1],
              title: offerTypeList[1]?.title,
              icon: <HouseKeyIcon />,
            },
          ]}
          multiple
          selected={chosenOfferType}
          onChange={setOfferVal}
        />

        {!isMap && hasMapSelection && (
          <Box sx={{ width: '100%' }}>
            <Box
              component="button"
              type="button"
              onClick={() => setIsMapExpanded((v) => !v)}
              sx={{
                width: '100%',
                height: '40px',
                padding: '0 12px 0 16px',
                border: `1px solid ${isMapExpanded ? COLORS.orange : COLORS['neutral/300']}`,
                borderRadius: isMapExpanded ? '4px 4px 0 0' : '4px',
                background: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: COLORS['secondary/700'],
                '&:hover': { borderColor: COLORS.orange },
              }}
            >
              <TabMapIcon isActive />
              <Typography fontSize={13} fontWeight={600} noWrap>
                {t('searchResult.seeOnMap')}
              </Typography>
              <ArrowDown
                style={{
                  marginLeft: 'auto',
                  transform: isMapExpanded ? 'rotate(180deg)' : undefined,
                  transition: 'transform 0.2s',
                }}
              />
            </Box>
            {isMapExpanded && (
              <Box
                sx={{
                  border: `1px solid ${COLORS.orange}`,
                  borderTop: 'none',
                  borderRadius: '0 0 4px 4px',
                  overflow: 'hidden',
                }}
              >
                <Box>
                  <FilterMapPreview mapArea={mapArea} admunitIds={admunitIds} height={120} />
                </Box>
                <Box
                  sx={{ padding: '6px 12px 8px', borderTop: `1px solid ${COLORS['neutral/300']}` }}
                >
                  <Typography
                    component="button"
                    type="button"
                    onClick={onClearMapFilters}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: 13,
                      fontWeight: 500,
                      color: COLORS['primary/600'],
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      width: '100%',
                      justifyContent: 'center',
                      '&:hover': { color: COLORS['primary/700'] },
                    }}
                  >
                    <FilterResetIcon style={{ color: COLORS['primary/600'] }} />
                    {t('searchResult.clearMapSelection')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {!hasMapSelection && (
          <LocationAutocomplete
            value={locations}
            onChange={onLocationsChange}
            label={t('mainHero.filter.searchPlaceholder')}
            placeholder={t('searchResult.searchPlaceholder')}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            gap: '20px',
            flexDirection: useMobileDrawer ? 'column' : { xs: 'column', sm: 'row' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CheckboxGroupMenu
              items={categories}
              value={catVal}
              onChange={setCatVal}
              label={t('searchResult.group')}
              isSelect
              hideLabel
              width="100%"
              order={CATEGORY_ORDER}
            />
          </Box>

          {!isMap && !hasMapSelection && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <LandMultilevelCheckboxList
                unitList={unitList}
                unitListValue={unitVal}
                setUnitListValue={setUnitVal}
                label={t('searchResult.countryState')}
                isSelect={!useMobileDrawer}
                width="100%"
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: '20px', flexDirection: 'column' }}>
          {rangesDimensions.area[0] !== undefined && rangesDimensions.area[1] !== undefined && (
            <Range
              isArea
              label={t('searchResult.area')}
              minValue={rangesDimensions.area[0] || 0}
              maxValue={rangesDimensions.area[1]}
              value={areaVal}
              setValue={setAreaVal}
              step={1}
              isAccordion={false}
            />
          )}

          {rangesDimensions.roomNumber[0] !== undefined &&
            rangesDimensions.roomNumber[1] !== undefined &&
            !effectiveIsLand && (
              <Range
                label={t('searchResult.rooms')}
                minValue={rangesDimensions.roomNumber[0] || 0}
                maxValue={getClampToMax(
                  rangesDimensions.roomNumber[1],
                  MAX_FILTERS_RANGE_VALUES.ROOMS,
                )}
                value={roomVal}
                setValue={setRoomVal}
                step={1}
                isAccordion={false}
              />
            )}
          {rangesDimensions.price[0] !== undefined &&
            rangesDimensions.price[1] !== undefined &&
            offerVal === OfferType.SELL && (
              <Range
                isPrice
                label={t('searchResult.purchasePrice')}
                minValue={rangesDimensions.price[0] || 0}
                maxValue={getClampToMax(
                  rangesDimensions.price[1],
                  MAX_FILTERS_RANGE_VALUES.SELL_PRICE,
                )}
                value={priceVal}
                setValue={setPriceVal}
                step={1}
                isAccordion={false}
              />
            )}
          {rangesDimensions.rentPrice[0] !== undefined &&
            rangesDimensions.rentPrice[1] !== undefined &&
            offerVal === OfferType.RENT && (
              <Range
                isPrice
                label={t('searchResult.purchasePrice')}
                minValue={rangesDimensions.rentPrice[0] || 0}
                maxValue={getClampToMax(
                  rangesDimensions.rentPrice[1],
                  MAX_FILTERS_RANGE_VALUES.RENT_PRICE,
                )}
                value={rentVal}
                setValue={setRentVal}
                step={1}
                isAccordion={false}
              />
            )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '20px',
            flexDirection: useMobileDrawer ? 'column' : { xs: 'column', sm: 'row' },
            '& > *': { flex: '1 1 0', minWidth: 0 },
          }}
        >
          {powerConsumptionClassesList.length > 0 && (
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <CheckboxGroupMenu
                items={powerConsumptionClassesList}
                value={powerConsumptionClassesList.filter((item) => hwbVal.includes(item.code))}
                onChange={(selected) => setHwbVal(selected.map((item) => item.code))}
                label={t('searchResult.hwb_title')}
                defaultOpen={false}
                hint={t('searchResult.hints.hwb')}
              />
            </Box>
          )}
          {energyClassFactorsList.length > 0 && (
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <CheckboxGroupMenu
                items={energyClassFactorsList}
                value={energyClassFactorsList.filter((item) => fgeeVal.includes(item.code))}
                onChange={(selected) => setFgeeVal(selected.map((item) => item.code))}
                label={t('searchResult.fgee_title')}
                defaultOpen={false}
                hint={t('searchResult.hints.fgee')}
              />
            </Box>
          )}
        </Box>

        <IconToggleGroup
          options={equipment.map((e, i) => ({
            key: i,
            value: e,
            title: e.title,
            ...(e.code === 'disabledFriendly.true' && { icon: <BarrierefreiFilterIcon /> }),
            ...(e.code === 'oldBuilding.true' && { icon: <AltbauFilterIcon /> }),
            ...(e.code === 'hasBalcony.true' && { icon: <BalkonFilterIcon /> }),
            ...(e.code === 'firstUsage.true' && { icon: <NeubauFilterIcon /> }),
            ...(e.code === 'hasParking.true' && { icon: <GarageFilterIcon /> }),
            ...(e.code === 'hasGarden.true' && { icon: <GartenFilterIcon /> }),
            ...(e.code === 'hasCellar.true' && { icon: <KellerFilterIcon /> }),
            ...(e.code === 'hasKitchen.true' && { icon: <KucheFilterIcon /> }),
            ...(e.code === 'hasLift.true' && { icon: <LiftFilterIcon /> }),
            ...(e.code === 'newBuilding.true' && { icon: <NeubauFilterIcon /> }),
            ...(e.code === 'hasTerrace.true' && { icon: <TerrasseFilterIcon /> }),
          }))}
          multiple
          selected={equipVal}
          onChange={setEquipVal}
          isEqual={(a, b) => a.code === b.code}
          label={t('searchResult.equipment')}
        />
      </ModalContent>

      <Box sx={{ padding: '0 24px 20px', flexShrink: 0 }}>
        {useMobileDrawer ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography
              component="button"
              type="button"
              disabled={!canReset}
              onClick={() => {
                onFullResetFilters();
                handleClose();
              }}
              sx={{
                fontSize: 14,
                color: canReset ? COLORS['primary/600'] : COLORS['neutral/400'],
                cursor: canReset ? 'pointer' : 'default',
                background: 'none',
                border: 'none',
                padding: 0,
                alignSelf: 'flex-start',
              }}
            >
              {t('searchResult.resetFilters')}
            </Typography>
            <StyledControlButton variant="primary" fullWidth onClick={handleClose} sx={{ mt: 0.5 }}>
              {t('searchResult.resultsFound', {
                count: new Intl.NumberFormat('de-DE').format(numFound ?? 0),
              })}
            </StyledControlButton>
          </Box>
        ) : (
          <ModalControlBox>
            <StyledControlButton
              variant="form-button"
              startIcon={<ClearFilterIcon />}
              onClick={onFullResetFilters}
              disabled={!canReset}
              sx={{
                bgcolor: COLORS['secondary/50'],
                color: COLORS['secondary/800'],
                border: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: COLORS['secondary/50'], border: 'none' },
                '& .MuiButton-startIcon': {
                  marginRight: '6px',
                  color: COLORS['secondary/500'],
                  '& svg': { width: 16, height: 16 },
                },
              }}
            >
              <Typography noWrap fontWeight={600}>
                {t('searchResult.clear')}
              </Typography>
            </StyledControlButton>
            <StyledControlButton variant="primary" onClick={handleClose}>
              {t('housingCardDetails.ok')}
            </StyledControlButton>
          </ModalControlBox>
        )}
      </Box>
    </>
  );

  const badgeSx = {
    '& .MuiBadge-badge': {
      fontSize: 11,
      fontWeight: 700,
      minWidth: 18,
      height: 18,
      padding: '0 4px',
      bgcolor: COLORS['secondary/500'],
      color: '#fff',
    },
  };

  const filterTrigger = useMobileDrawer ? (
    <Badge badgeContent={activeFiltersCount} color="default" sx={badgeSx}>
      <IconButton
        onClick={handleOpen}
        disabled={disabled}
        aria-label={t('searchResult.filter')}
        sx={{
          height: 36,
          border: `1px solid ${COLORS['neutral/300']}`,
          borderRadius: '10px',
          bgcolor: COLORS.white,
          flexShrink: 0,
          gap: '6px',
          px: '10px',
          '&:hover': { bgcolor: COLORS['neutral/50'] },
        }}
      >
        <FiltersIcon active={activeFiltersCount > 0} />
        <Typography fontSize={14} fontWeight={500} color={COLORS['primary/900']} noWrap>
          {t('searchResult.filter')}
        </Typography>
      </IconButton>
    </Badge>
  ) : (
    <Badge badgeContent={activeFiltersCount} color="default" sx={badgeSx}>
      <StyledButton
        variant="form-button"
        onClick={handleOpen}
        disabled={disabled}
        startIcon={<FiltersIcon />}
      >
        <Typography noWrap fontWeight={600} color={COLORS['secondary/700']}>
          {t('searchResult.moreFilters')}
        </Typography>
      </StyledButton>
    </Badge>
  );

  return (
    <FiltersBox>
      <Box>{filterTrigger}</Box>

      {useMobileDrawer ? (
        <Drawer
          anchor="left"
          open={isOpen}
          onClose={handleClose}
          disableScrollLock
          slotProps={{
            backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.3)' } },
          }}
          PaperProps={{
            sx: {
              width: 'min(320px, 85vw)',
              maxWidth: '100%',
              boxSizing: 'border-box',
              boxShadow: '4px 0 24px rgba(0,0,0,0.12)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          }}
        >
          {filterContent}
        </Drawer>
      ) : (
        <Modal open={isOpen} onClose={handleClose}>
          <ModalWrapper>{filterContent}</ModalWrapper>
        </Modal>
      )}
    </FiltersBox>
  );
};
